import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ThumbsUp, Flame, PartyPopper, Laugh, Sparkles, Frown, MessageCircle, Trash2, Send, Flag, ImagePlus, X, CornerDownRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Avatar, EmptyState, Spinner } from "@/components/Bits";
import { Link, useSearchParams } from "react-router-dom";
import { MentionText, MentionList, useMentions, memberPath } from "@/components/Mentions";
import AdSlot from "@/components/AdSlot";

const REACT = [["like", ThumbsUp, "J'aime"], ["fire", Flame, "En feu"], ["clap", PartyPopper, "Bravo"], ["laugh", Laugh, "Trop drôle"], ["wow", Sparkles, "Wow"], ["ouch", Frown, "Aïe"]];
export const gifUrl = (id) => `https://media.giphy.com/media/${id}/200.gif`;

export function eventText(e) {
  const d = e.data || {};
  switch (e.type) {
    case "member_joined": return `${d.name} a rejoint la ligue.`;
    case "league_created": return `${d.name} a créé la ligue « ${d.league} ».`;
    case "competition_started": return "La compétition démarre : la ligue compte 4 membres !";
    case "challenge_created": return `${d.name} lance le défi « ${d.title} » (${d.period}).`;
    case "challenge_accepted": return `Défi accepté par tous : « ${d.title} ».`;
    case "challenge_end_changed": return `${d.name} a changé la fin du défi « ${d.title} » : ${d.period}.`;
    case "challenge_finished": return `Défi terminé : « ${d.title} »${d.names?.length ? ` — ${d.condition === "last" ? "perdant" : d.condition === "first" ? "gagnant" : "désigné"} : ${d.names.join(", ")}` : ""}.`;
    case "challenge_done": return `« ${d.title} » a été réalisé. Bravo !`;
    case "leader_changed": return `${d.name} vient de prendre la tête de la ligue !`;
    case "social_bonus": return `${d.name} suit @mapetiteligue sur ${d.network === "x" ? "X" : "Instagram"} : +${d.points} pts au classement de la saison.`;
    case "referral_bonus": return `${d.guest} a rejoint la ligue grâce à ${d.name} : +${d.points} pts de parrainage !`;
    case "week_king": return `Nouveau roi de la semaine (${d.week}) : ${d.names}, avec ${d.nights} soirée${d.nights > 1 ? "s" : ""} gagnée${d.nights > 1 ? "s" : ""} !`;
    case "supporters_thanks": {
      const s = d.supporters || [];
      const names = s.map((p) => p.name);
      const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} et ${names[names.length - 1]}` : names[0];
      return `Merci aux supporters de la semaine ! ${list} ${s.length > 1 ? "suivent" : "suit"} désormais @mapetiteligue. Toi aussi : +15 pts par réseau (Instagram et X).`;
    }
    case "last_changed": return `${d.name} passe dernier… le prochain défi se rapproche !`;
    case "big_climb": return `${d.name} gagne ${d.places} places et remonte au ${d.rank}e rang.`;
    case "streak_bonus": return `${d.name} réussit ${d.streak} pronostics de suite ! (+${d.points} bonus)`;
    case "period_end": return `Fin de ${d.period} : ${d.champion} champion, ${d.last} dernier.`;
    case "rules_updated": return `Le barème a été modifié (version ${d.version}).`;
    case "activity_proposed": return `${d.name} propose une date pour l'activité : ${d.date}${d.note ? ` — ${d.note}` : ""}.`;
    case "message": return d.text || (d.gif_id ? "a partagé un GIF" : "");
    default: return "Activité";
  }
}

function GifPicker({ onPick, onClose }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const search = useCallback((term) => {
    api.get("/gifs/search", { params: { q: term } }).then(({ data }) => setList(data)).catch((e) => setErr(errMsg(e)));
  }, []);
  useEffect(() => { search(""); }, [search]);
  return (
    <div className="mt-2 rounded-xl border border-white/10 bg-[#05070D] p-2" data-testid="gif-picker">
      <form onSubmit={(e) => { e.preventDefault(); search(q); }} className="flex gap-2">
        <input aria-label="Rechercher un GIF" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un GIF…" className="mpl-input py-1.5 text-xs" data-testid="gif-search-input" />
        <button type="button" onClick={onClose} aria-label="Fermer la recherche de GIF" className="text-slate-400"><X size={16} /></button>
      </form>
      {err ? <p className="mt-2 text-xs text-amber-300">{err}</p> : !list ? <Spinner /> : (
        <div className="mt-2 grid max-h-48 grid-cols-3 gap-1.5 overflow-y-auto">
          {list.map((g) => (
            <button type="button" key={g.id} onClick={() => onPick(g.id)} aria-label={`Choisir le GIF ${g.title || g.id}`} data-testid={`gif-option-${g.id}`}>
              <img src={gifUrl(g.id)} alt={g.title || "GIF"} loading="lazy" className="h-20 w-full rounded-md object-cover" />
            </button>
          ))}
        </div>
      )}
      <p className="mt-1 text-right text-[10px] text-slate-500">Propulsé par GIPHY</p>
    </div>
  );
}

function Composer({ onSend, placeholder, testId, compact, members = [], meId, replyTo, onCancelReply }) {
  const [text, setText] = useState("");
  const [gif, setGif] = useState(null);
  const [picker, setPicker] = useState(false);
  const ref = useRef(null);
  const mt = useMentions(members, meId);
  useEffect(() => {
    if (!replyTo) { return; }
    const own = replyTo.user_id === meId;
    if (!own) { mt.add({ id: replyTo.user_id, display_name: replyTo.display_name, username: replyTo.username }); }
    const start = own ? "" : `@${replyTo.username || replyTo.display_name} `;
    setText((t) => (t.includes(start) ? t : start + t));
    requestAnimationFrame(() => { const el = ref.current; if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); } });
  }, [replyTo?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !gif) return;
    if (await onSend({ body: text, gif_id: gif, mentions: mt.ids(text), reply_to: replyTo?.id || null })) { setText(""); setGif(null); setPicker(false); mt.reset(); onCancelReply?.(); }
  };
  const onKeyDown = (e) => {
    if (!mt.options.length) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); mt.setActive((mt.active + (e.key === "ArrowDown" ? 1 : -1) + mt.options.length) % mt.options.length); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); mt.choose(mt.options[mt.active], text, ref.current, setText); }
    else if (e.key === "Escape") mt.close();
  };
  return (
    <form onSubmit={submit} className="min-w-0 flex-1">
      {replyTo && (
        <p className="mb-1.5 flex items-center gap-2 text-[11px] text-slate-400" data-testid={`${testId}-replying`}>
          <CornerDownRight size={12} className="text-[#B7F52A]" /> Réponse à <b className="text-slate-200">{replyTo.display_name}</b>
          <button type="button" onClick={() => { onCancelReply(); setText(""); mt.reset(); }} aria-label="Annuler la réponse" className="text-slate-500 hover:text-white" data-testid={`${testId}-reply-cancel`}><X size={12} /></button>
        </p>
      )}
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <input ref={ref} aria-label={placeholder} value={text} onKeyDown={onKeyDown} onBlur={() => setTimeout(mt.close, 150)}
            onChange={(e) => { setText(e.target.value); mt.track(e.target.value, e.target.selectionStart); }} maxLength={280} placeholder={placeholder}
            className={`mpl-input ${compact ? "py-2 text-xs" : ""}`} autoComplete="off" data-testid={`${testId}-input`} />
          <MentionList options={mt.options} active={mt.active} testId={testId} up={compact} onPick={(m) => mt.choose(m, text, ref.current, setText)} />
        </div>
        <button type="button" onClick={() => setPicker(!picker)} aria-label="Ajouter un GIF" aria-expanded={picker} className="shrink-0 text-slate-400 hover:text-[#B7F52A]" data-testid={`${testId}-gif`}><ImagePlus size={18} /></button>
        <button className="mpl-btn-primary shrink-0 px-3" aria-label="Publier" data-testid={`${testId}-send`}>{compact ? <Send size={14} /> : <><Send size={14} className="sm:hidden" /><span className="hidden sm:inline">Publier</span></>}</button>
      </div>
      {gif && <div className="relative mt-2 inline-block"><img src={gifUrl(gif)} alt="GIF sélectionné" className="h-24 rounded-lg" /><button type="button" aria-label="Retirer le GIF" onClick={() => setGif(null)} className="absolute right-1 top-1 rounded-full bg-black/70 p-0.5"><X size={12} /></button></div>}
      {picker && <GifPicker onPick={(id) => { setGif(id); setPicker(false); }} onClose={() => setPicker(false)} />}
    </form>
  );
}

function Event({ e, reload, canPost, members, meId, leagueId, focus }) {
  const [open, setOpen] = useState(focus?.evt === e.id);
  const [replyTo, setReplyTo] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (focus?.evt !== e.id) return undefined;
    setOpen(true);
    const t = setTimeout(() => {
      const el = (focus.c && document.querySelector(`[data-testid="comment-${focus.c}"]`)) || ref.current;
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("mpl-focus-flash");
      setTimeout(() => el.classList.remove("mpl-focus-flash"), 2600);
    }, 250);
    return () => clearTimeout(t);
  }, [focus, e.id]);
  const act = async (fn) => { try { await fn(); reload(); return true; } catch (err) { toast.error(errMsg(err)); return false; } };
  return (
    <article ref={ref} className="mpl-card p-4" data-testid={`activity-${e.id}`}>
      <div className="flex items-start gap-3">
        {e.type === "message" && e.actor ? <Avatar user={e.actor} size={32} /> : <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#B7F52A]" />}
        <div className="min-w-0 flex-1">
          {e.type === "message" && e.actor && <Link to={memberPath(leagueId, e.actor.id)} className="text-xs font-bold text-white hover:text-[#B7F52A]" data-testid={`feed-author-${e.id}`}>{e.actor.display_name}{e.actor.username && <span className="ml-1 font-normal text-slate-400">@{e.actor.username}</span>}</Link>}
          {e.type === "message" ? (e.data?.text && <p className="break-words text-sm text-slate-200"><MentionText text={e.data.text} mentions={e.data.mentions} leagueId={leagueId} /></p>)
            : eventText(e) && <p className="text-sm text-slate-200">{eventText(e)}</p>}
          {e.data?.gif_id && <img src={gifUrl(e.data.gif_id)} alt="GIF publié" loading="lazy" className="mt-2 max-h-48 max-w-full rounded-lg" data-testid={`activity-gif-${e.id}`} />}
          <p className="mt-1 text-[11px] text-slate-500">{fmtDateTime(e.created_at)}</p>
        </div>
        {e.can_delete && <button onClick={() => act(() => api.delete(`/activity/${e.id}`))} aria-label="Supprimer la publication" data-testid={`activity-delete-${e.id}`} className="text-slate-500 hover:text-red-300"><Trash2 size={14} /></button>}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {REACT.map(([k, Icon, label]) => {
          const on = e.my_reactions.includes(k);
          return (
            <button key={k} disabled={!canPost} onClick={() => act(() => api.post(`/activity/${e.id}/reactions`, { kind: k }))} data-testid={`react-${k}-${e.id}`}
              aria-label={`${label}${on ? " (retirer)" : ""}`} aria-pressed={on} title={label}
              className={`flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-semibold ${on ? "border-[#B7F52A]/50 bg-[#B7F52A]/10 text-[#B7F52A]" : "border-white/10 text-slate-400 hover:text-white"}`}>
              <Icon size={13} /> {e.reactions[k] || ""}
            </button>
          );
        })}
        <button onClick={() => setOpen(!open)} aria-expanded={open} aria-label={`Commentaires (${e.comments.length})`} data-testid={`comments-toggle-${e.id}`} className="ml-auto flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-white"><MessageCircle size={13} /> {e.comments.length}</button>
      </div>
      {open && (
        <div className="mt-3 space-y-2 border-t border-white/[0.05] pt-3">
          {e.comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2 text-xs" data-testid={`comment-${c.id}`}>
              <Avatar user={c.author} size={22} />
              <div className="min-w-0 flex-1">
                {c.reply_to && <p className="flex items-center gap-1 text-[10px] text-slate-500" data-testid={`comment-reply-to-${c.id}`}><CornerDownRight size={10} /> en réponse à {c.reply_to.display_name}</p>}
                <p><Link to={memberPath(leagueId, c.user_id)} className="font-bold text-white hover:text-[#B7F52A]" data-testid={`comment-author-${c.id}`}>{c.author?.display_name}</Link> <span className="break-words text-slate-300"><MentionText text={c.body} mentions={c.mentions} leagueId={leagueId} /></span></p>
                {c.gif_id && <img src={gifUrl(c.gif_id)} alt="GIF" loading="lazy" className="mt-1 max-h-32 rounded-md" />}
                <p className="text-[10px] text-slate-500">{fmtDateTime(c.created_at)}{c.reported ? " · signalé" : ""}
                  {canPost && <button type="button" onClick={() => setReplyTo({ id: c.id, user_id: c.user_id, display_name: c.author?.display_name || "Membre", username: c.author?.username })}
                    className="ml-1 rounded px-1.5 py-1.5 font-semibold text-slate-400 hover:text-[#B7F52A]" data-testid={`comment-reply-${c.id}`}>Répondre</button>}</p>
              </div>
              {c.can_report && canPost && <button onClick={() => act(() => api.post(`/comments/${c.id}/report`)).then((ok) => ok && toast.success("Commentaire signalé au propriétaire"))} aria-label="Signaler ce commentaire" data-testid={`comment-report-${c.id}`} className="text-slate-500 hover:text-amber-300"><Flag size={12} /></button>}
              {c.can_delete && <button onClick={() => act(() => api.delete(`/comments/${c.id}`))} aria-label="Supprimer le commentaire" data-testid={`comment-delete-${c.id}`} className="text-slate-500 hover:text-red-300"><Trash2 size={12} /></button>}
            </div>
          ))}
          {canPost && <Composer compact placeholder="Un petit mot… (@ pour mentionner)" testId={`comment-${e.id}`} members={members} meId={meId} replyTo={replyTo} onCancelReply={() => setReplyTo(null)} onSend={(b) => act(() => api.post(`/activity/${e.id}/comments`, b))} />}
        </div>
      )}
    </article>
  );
}

export default function ActivityTab({ league }) {
  const { user } = useAuth();
  const [feed, setFeed] = useState(null);
  const [members, setMembers] = useState([]);
  const [params] = useSearchParams();
  const focus = useMemo(() => (params.get("evt") ? { evt: params.get("evt"), c: params.get("c") } : null), [params]);
  const load = useCallback(() => { api.get(`/leagues/${league.id}/activity`).then(({ data }) => setFeed(data)); }, [league.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (feed && focus?.evt && !feed.some((e) => e.id === focus.evt)) toast("Ce message est trop ancien pour être affiché dans le fil.");
  }, [feed, focus]);
  useEffect(() => { api.get(`/leagues/${league.id}/members`).then(({ data }) => setMembers(Array.isArray(data) ? data : data.members || [])).catch(() => {}); }, [league.id]);
  const send = async (b) => { try { await api.post(`/leagues/${league.id}/activity`, b); load(); return true; } catch (err) { toast.error(errMsg(err)); return false; } };
  if (!feed) return <Spinner />;
  return (
    <div className="space-y-3" data-testid="activity-feed">
      <AdSlot placement="sponsor_mention" leagueId={league.id} mention />
      {!league.archived && (
        <div className="mpl-card flex items-start gap-2 p-3">
          <Avatar user={user} size={32} />
          <Composer placeholder="Un message ? @ pour mentionner" testId="activity-message" onSend={send} members={members} meId={user.id} />
        </div>
      )}
      {feed.length ? feed.map((e) => <Event key={e.id} e={e} reload={load} canPost={!league.archived} members={members} meId={user.id} leagueId={league.id} focus={focus} />) : <EmptyState icon={Flame} title="Le fil est calme" text="Les arrivées, défis et changements au classement apparaîtront ici." testId="activity-empty" />}
    </div>
  );
}
